import { Extension } from '@tiptap/core';
import { Plugin, PluginKey } from '@tiptap/pm/state';
import { TableMap, columnResizingPluginKey } from '@tiptap/pm/tables';
import { measureColumnWidths } from '@/components/content/tiptap/measureColumnWidths';
import { applyColumnPercentages } from '@/lib/applyColumnPercentages';
import { PDF_PAGE_GEOMETRY } from '@/constants';

/**
 * Column dragging that cannot change the table's width.
 *
 * This plugin holds the table's total width constant by moving width between the dragged column and
 * the one immediately to its right, so:
 *   - every other column keeps its width, and
 *   - a drag can travel at most until the next column reaches MIN_COLUMN_WIDTH.
 *
 * The companion piece is `BoundedTableView.js`, which keeps a table inside its container in every
 * other state - on load, and after any edit that is not a drag. Between them, a table's rendered
 * width never exceeds the box it is in.
 */

const boundedColumnResizeKey = new PluginKey('boundedColumnResize');

export const MIN_COLUMN_WIDTH = 25;

/**
 * The pixel minimum to enforce for a drag, given the table's current rendered width.
 * Never scales DOWN: in an editor narrower than the page, 25px stays 25px.
 */
export const minimumForRenderedWidth = (renderedTableWidth) => {
  if (!Number.isFinite(renderedTableWidth) || renderedTableWidth <= 0) {
    return MIN_COLUMN_WIDTH;
  }

  const scale = renderedTableWidth / PDF_PAGE_GEOMETRY.contentWidth;

  return scale > 1 ? MIN_COLUMN_WIDTH * scale : MIN_COLUMN_WIDTH;
};

export const resolvePairWidths = (pairTotal, desiredLeftWidth, minWidth = MIN_COLUMN_WIDTH) => {
  // A pair too narrow to split fairly keeps an even share rather than forcing a negative width.
  if (pairTotal < minWidth * 2) {
    return [pairTotal / 2, pairTotal / 2];
  }

  const left = Math.min(Math.max(desiredLeftWidth, minWidth), pairTotal - minWidth);

  return [left, pairTotal - left];
};

const findTableDom = (view, cellPos) => {
  let dom = view.domAtPos(cellPos).node;

  while (dom && dom.nodeName !== 'TABLE') {
    dom = dom.parentNode;
  }

  return dom;
};

const paintColumnWidths = (tableDom, widths) => {
  const colgroup = tableDom.querySelector('colgroup');

  if (!colgroup || !applyColumnPercentages(colgroup, widths)) {
    return;
  }

  tableDom.style.setProperty('width', '100%');
  // `min-width` outranks `max-width`, so it must be cleared rather than merely overridden.
  tableDom.style.removeProperty('min-width');
};

const commitColumnWidths = (view, tablePos, tableNode, widths) => {
  const map = TableMap.get(tableNode);
  const { tr } = view.state;
  const tableStart = tablePos + 1;
  let changed = false;

  for (let row = 0; row < map.height; row += 1) {
    for (let col = 0; col < map.width; col += 1) {
      const mapIndex = row * map.width + col;

      if (row && map.map[mapIndex] === map.map[mapIndex - map.width]) {
        // eslint-disable-next-line no-continue
        continue;
      }

      if (col && map.map[mapIndex] === map.map[mapIndex - 1]) {
        // eslint-disable-next-line no-continue
        continue;
      }

      const pos = map.map[mapIndex];
      const cell = tableNode.nodeAt(pos);

      if (!cell) {
        // eslint-disable-next-line no-continue
        continue;
      }

      const { colspan } = cell.attrs;
      const colwidth = [];

      for (let span = 0; span < colspan; span += 1) {
        colwidth.push(Math.round(widths[col + span] || MIN_COLUMN_WIDTH));
      }

      tr.setNodeMarkup(tableStart + pos, null, { ...cell.attrs, colwidth });
      changed = true;
    }
  }

  if (changed) {
    view.dispatch(tr);
  }
};

const startBoundedDrag = (view, event) => {
  const resizeState = columnResizingPluginKey.getState(view.state);

  if (!resizeState || resizeState.activeHandle === -1 || resizeState.dragging) {
    return false;
  }

  const $cell = view.state.doc.resolve(resizeState.activeHandle);
  const tableNode = $cell.node(-1);
  const tableStart = $cell.start(-1);
  const map = TableMap.get(tableNode);
  const col = map.colCount($cell.pos - tableStart) + $cell.nodeAfter.attrs.colspan - 1;

  // No right-hand neighbour to trade width with, so this plugin does not apply. Decline the event
  if (col >= map.width - 1) {
    return false;
  }

  const tableDom = findTableDom(view, $cell.start(-1));

  if (!tableDom) {
    return false;
  }

  const widths = measureColumnWidths(tableDom);

  if (widths.length !== map.width) {
    return false;
  }

  const startX = event.clientX;
  const startLeftWidth = widths[col];
  const pairTotal = widths[col] + widths[col + 1];
  const win = view.dom.ownerDocument.defaultView || window;
  const live = widths.slice();

  const minWidth = minimumForRenderedWidth(widths.reduce((sum, width) => sum + width, 0));

  const applyAt = (clientX) => {
    const [left, right] = resolvePairWidths(
      pairTotal,
      startLeftWidth + (clientX - startX),
      minWidth
    );

    live[col] = left;
    live[col + 1] = right;
    paintColumnWidths(tableDom, live);
  };

  const onMove = (moveEvent) => {
    if (!moveEvent.which) {
      // eslint-disable-next-line no-use-before-define
      onUp(moveEvent);
      return;
    }
    applyAt(moveEvent.clientX);
  };

  function onUp(upEvent) {
    win.removeEventListener('mousemove', onMove);
    win.removeEventListener('mouseup', onUp);
    applyAt(upEvent.clientX);
    commitColumnWidths(view, $cell.before(-1), tableNode, live);
  }

  win.addEventListener('mousemove', onMove);
  win.addEventListener('mouseup', onUp);

  event.preventDefault();
  return true;
};

export const BoundedColumnResize = Extension.create({
  name: 'boundedColumnResize',
  priority: 1000,

  addProseMirrorPlugins() {
    return [
      new Plugin({
        key: boundedColumnResizeKey,
        props: {
          handleDOMEvents: {
            // Returning true prevents prosemirror-tables' own mousedown handler from running.
            mousedown: (view, event) => startBoundedDrag(view, event)
          }
        }
      })
    ];
  }
});

export default BoundedColumnResize;
